import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <span className="font-semibold">Legal</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none">
          <h1 className="text-3xl font-bold mb-2">Terms of Service (TOS)</h1>
          <p className="text-muted-foreground mb-8">Last Updated: January 7, 2024</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using YASNO AI ("Service"), operated by YASNO Marketing LLC ("we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">2. Service Description</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              YASNO AI is an automated AI-powered marketing platform that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Creates, schedules, and publishes marketing content across social media platforms</li>
              <li>Uses voice and text commands to generate complete marketing campaigns</li>
              <li>Manages social media accounts on your behalf</li>
              <li>Generates AI-created content including text, images, and videos</li>
              <li>Operates in "Full Auto Mode" where content may be published without human review</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">3. AUTHORIZATION & CONSENT</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">3.1 Explicit Digital Representative Authorization</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:</p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <p className="text-muted-foreground italic leading-relaxed">
                "I hereby explicitly authorize YASNO Marketing LLC and its AI Agents to act as my digital representative with full operational authority. I grant them the power to create and manage social media accounts, generate multilingual content, and publish posts autonomously on my behalf based solely on my voice and/or text instructions.
              </p>
              <p className="text-muted-foreground italic leading-relaxed mt-4">
                I ACCEPT FULL AND UNCONDITIONAL RESPONSIBILITY FOR ALL INSTRUCTIONS PROVIDED and explicitly acknowledge that "Full Auto Mode" involves publishing content without manual human review.
              </p>
              <p className="text-muted-foreground italic leading-relaxed mt-4">
                By using this Service, I confirm that I understand and accept all risks associated with autonomous AI content generation and publication, including but not limited to content inaccuracies, cultural missteps, platform violations, or unintended consequences from AI-generated content."
              </p>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">3.2 Scope of Authorization</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">Your authorization includes but is not limited to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Complete social media account management</li>
              <li>Autonomous content creation and publication</li>
              <li>Strategic decision-making based on AI analysis</li>
              <li>Response to engagement and comments</li>
              <li>Platform-specific optimizations and adjustments</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">4. User Account and Registration</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">4.1 Account Creation</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You must create an account to use the Service. You agree to provide accurate, current, and complete information during registration.
            </p>

            <h3 className="text-lg font-medium mb-3 text-foreground">4.2 Account Security</h3>
            <p className="text-muted-foreground leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">5. THE "KILL SWITCH" & TERMINATION</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">5.1 Immediate Agent Revocation</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">Users retain complete control through the "KILL SWITCH" functionality:</p>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <p className="text-muted-foreground leading-relaxed">
                At any time, you may immediately revoke all AI Agents' authority via the "STOP ALL AGENTS" button prominently displayed in your dashboard. Upon activation:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                <li>All AI operations cease immediately</li>
                <li>API tokens are revoked and social media access is terminated</li>
                <li>All scheduled but unposted content is permanently deleted</li>
                <li>In-progress content generation is halted</li>
                <li>Social media accounts revert to manual control only</li>
              </ul>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">5.2 Termination Effects</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">Upon termination (whether by user or us):</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>IMMEDIATE CESSATION:</strong> All automated activities stop</li>
              <li><strong>DATA RETENTION:</strong> Account data is retained for 30 days for recovery purposes</li>
              <li><strong>CONTENT REMOVAL:</strong> All queued content is permanently deleted from our systems</li>
              <li><strong>TOKEN REVOCATION:</strong> Social media platform tokens are revoked</li>
              <li><strong>PUBLISHED CONTENT:</strong> Content already published remains on social platforms but management ceases</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">5.3 No Liability for Kill Switch Activation</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We are not liable for:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Loss of content scheduled but not yet published</li>
              <li>Interruption of ongoing campaigns</li>
              <li>Any consequences of immediate termination</li>
              <li>Platform-specific effects of token revocation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">6. Social Media Management Authorization</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">6.1 Explicit Authorization</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">By connecting your social media accounts to YASNO AI, you expressly authorize us to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Access, manage, and post content to your connected social media accounts</li>
              <li>Modify account settings related to content posting and scheduling</li>
              <li>Respond to comments and messages (if enabled in your settings)</li>
              <li>Analyze performance metrics and engagement data</li>
              <li>Make adjustments to posting schedules and strategies based on AI analysis</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">6.2 Setup of New Social Media Accounts</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">For users who request setup of new social media accounts:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You authorize us to create accounts on your behalf on specified platforms</li>
              <li>You provide us with necessary information (business name, contact info, etc.)</li>
              <li>You grant us administrative access during the setup and optimization period</li>
              <li>You agree to take full ownership of these accounts after setup completion</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">6.3 Platform Compliance</h3>
            <p className="text-muted-foreground leading-relaxed">
              You acknowledge that we will operate within the terms of service of each social media platform. <strong>YOU REMAIN SOLELY AND ULTIMATELY RESPONSIBLE FOR COMPLIANCE</strong> with each platform's policies and for any violations resulting from AI-generated content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">7. AI-Generated Content</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">7.1 FULL AUTO MODE ACKNOWLEDGMENT</h3>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
              <p className="text-muted-foreground leading-relaxed">
                <strong>YOU EXPRESSLY ACKNOWLEDGE THAT:</strong><br/>
                "In 'Full Auto Mode,' content is generated and published WITHOUT HUMAN REVIEW. I accept all risks associated with autonomous AI content creation, including potential inaccuracies, inappropriate content, or violations of platform policies. I understand that AI may misinterpret instructions or generate unexpected content."
              </p>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">7.2 Content Ownership</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">You retain ownership of all content generated through the Service, subject to the following:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>We maintain a non-exclusive license to use generated content for service improvement</li>
              <li>AI-generated content may incorporate licensed elements from our AI providers</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">7.3 Content Review Responsibility</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">While our AI systems are designed to create appropriate content, <strong>YOU BEAR FULL RESPONSIBILITY FOR:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>All content published on your behalf</li>
              <li>Compliance with all applicable laws and regulations</li>
              <li>Monitoring published content and notifying us of any issues</li>
              <li>The accuracy and appropriateness of AI-generated content</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">7.4 Prohibited Content</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">You agree not to use the Service to create content that:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Violates intellectual property rights</li>
              <li>Is defamatory, obscene, or harassing</li>
              <li>Promotes illegal activities</li>
              <li>Contains hate speech or discrimination</li>
              <li>Violates platform-specific content policies</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>YOU ACKNOWLEDGE THAT VIOLATIONS MAY RESULT IN PLATFORM SUSPENSIONS OR BANS FOR WHICH WE BEAR NO LIABILITY.</strong>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">8. Data and Privacy</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">8.1 Data Collection</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We collect:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Account and profile information</li>
              <li>Social media credentials and access tokens</li>
              <li>Content created through the Service</li>
              <li>Performance metrics and analytics</li>
              <li>Voice commands and usage patterns</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">8.2 Data Usage</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We use collected data to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide and improve the Service</li>
              <li>Train and enhance our AI models</li>
              <li>Generate analytics and reports</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">8.3 Data Security</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We implement industry-standard security measures to protect your data, including:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Encryption of sensitive information</li>
              <li>Regular security audits</li>
              <li>Access controls and monitoring</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">9. Fees and Payment</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">9.1 Subscription Plans</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Fees are billed monthly or annually in advance</li>
              <li>No refunds for partial months or unused services</li>
              <li>Prices subject to change with 30 days notice</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">9.2 AI Usage Credits</h3>
            <p className="text-muted-foreground leading-relaxed">
              Some plans include monthly AI generation credits. Unused credits do not roll over.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">10. Termination</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">10.1 By User</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">You may terminate your account at any time via the "KILL SWITCH" or standard termination. Upon termination:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>All scheduled posts will be canceled</li>
              <li>Access to historical data may be limited</li>
              <li>Content already published remains on social platforms</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">10.2 By Us</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We may suspend or terminate your account if:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You violate these Terms</li>
              <li>Payment is overdue</li>
              <li>Required for legal or security reasons</li>
              <li>Your usage threatens platform relationships</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">11. RISK ACKNOWLEDGMENT & DISCLAIMER OF WARRANTIES</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">11.1 EXPRESS RISK ACKNOWLEDGMENT</h3>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <p className="text-muted-foreground leading-relaxed mb-2"><strong>YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:</strong></p>
              <p className="text-muted-foreground italic leading-relaxed">
                "Using YASNO AI involves significant risks including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>AI misinterpretation of instructions</li>
                <li>Generation of inaccurate or inappropriate content</li>
                <li>Platform violations and account suspensions</li>
                <li>Cultural misunderstandings in multilingual content</li>
                <li>Legal liability for published content</li>
                <li>Financial losses from ineffective campaigns</li>
              </ul>
              <p className="text-muted-foreground italic leading-relaxed mt-2">
                I voluntarily assume all risks and waive any claims against YASNO Marketing LLC for AI-generated content or autonomous actions."
              </p>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">11.2 Disclaimer of Warranties</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, INCLUDING:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>ACCURACY OF AI-GENERATED CONTENT</li>
              <li>UPTIME OR AVAILABILITY GUARANTEES</li>
              <li>SUITABILITY FOR SPECIFIC PURPOSES</li>
              <li>COMPLIANCE WITH PLATFORM POLICIES</li>
              <li>ABSENCE OF ERRORS OR OMISSIONS</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">12. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>LOSS OF DATA OR CONTENT</li>
              <li>DAMAGES FROM SERVICE INTERRUPTION</li>
              <li>THIRD-PARTY CLAIMS RELATED TO YOUR CONTENT</li>
              <li>INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES</li>
              <li>PLATFORM SUSPENSIONS OR BANS</li>
              <li>BUSINESS LOSSES FROM INEFFECTIVE CAMPAIGNS</li>
              <li>AI-GENERATED CONTENT ISSUES</li>
              <li>"KILL SWITCH" ACTIVATION CONSEQUENCES</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE PREVIOUS 3 MONTHS.</strong>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">13. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS YASNO Marketing LLC from any claims, damages, losses, or expenses arising from:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Your use of the Service</li>
              <li>Content you create or publish</li>
              <li>Violation of these Terms</li>
              <li>Infringement of third-party rights</li>
              <li>Platform violations or suspensions</li>
              <li>Any consequences of AI-generated content</li>
              <li>Your instructions to AI Agents</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">14. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms at any time. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">15. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms are governed by the laws of Delaware, United States.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">16. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">For questions about these Terms:</p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-2">
              <p className="text-muted-foreground">YASNO Marketing LLC</p>
              <p className="text-muted-foreground">Email: support@yasnomktg.com</p>
            </div>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Button
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="rounded-full"
          >
            Back to Top
          </Button>
        </div>
      </main>
    </div>
  );
}
